import { useTranslation } from "react-i18next";
import NewPage from "../../../layouts/page/NewPage";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { convertBooleanToStatus } from "../../../../utils/ConverterUtils";
import MenuConfig from "../../../../constants/MenuConfig";
import Form from "./Form";
import { getId, prepareUploadFileData } from "../../../../utils/ApiUtils";
import { useNavigate } from "react-router-dom";
import Api from "../../../../constants/Api";
import { ApiError } from "../../../components/Common/ApiError";
import { formatDateToYYYYMMDD } from "../../../../utils/DateUtils";
import { useBranch } from "../../../../context/BranchContext";
import { sendUploadFile } from "../../../../services/AxiosService";
import { successAlert } from "../../../components/alert/defaultAlert";
import axiosInstance from "../../../../services/AxiosInstance";

const CustomerNew = () => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const [status, setStatus] = useState(true);
    const [entityData, setEntityData] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [customerRequirements, setCustomerRequirements] = useState({
        requireBranchInCustomer: false,
        requireCpfInCustomer: false
    });
    const navigate = useNavigate();

    // Define o schema de validação do form.
    const formSchema = Yup.object().shape({
        firstName: Yup.string()
            .max(30, t('validation.max_length', { max: 30 }))
            .min(3, t("validation.min.generic", { field: t('firstName'), qty: 3 }))
            .required(t('validation.required.firstName')),
        lastName: Yup.string()
            .nullable()
            .max(30, t('validation.max_length', { max: 30 }))
            .test('full-name-max-length', t('validation.max_full_name'), function (lastName) {
                const fullName = [this.parent.firstName, lastName]
                    .filter(part => part != null && part.trim() !== '')
                    .map(part => part.trim())
                    .join(' ');

                return Array.from(fullName).length <= 50;
            }),
        phone: Yup.string()
            .max(30, t('validation.max_length', { max: 30 }))
            .required(t('validation.required.phone')),
        document: Yup.string()
            .nullable()
            .test('required-document', t('validation.required.document'), (value) => {
                if (!customerRequirements.requireCpfInCustomer) {
                    return true;
                }

                return value?.replace(/\D/g, '').length > 0;
            })
            .test('valid-document-length', t('validation.invalid.cpf'), (value) => {
                const digits = value?.replace(/\D/g, '') || '';
                return digits.length === 0 || digits.length === 11;
            }),
        branch: Yup.object().nullable()
            .test('required-branch', t('validation.required.branch'), (value) => {
                if (!customerRequirements.requireBranchInCustomer) {
                    return true;
                }

                return value != null;
            })
    });

    useEffect(() => {
        axiosInstance.get(Api.parameter.main)
            .then((resp) => {
                const data = resp.data?.[0];

                setCustomerRequirements({
                    requireBranchInCustomer: !!data?.requireBranchInCustomer,
                    requireCpfInCustomer: !!data?.requireCpfInCustomer
                });
            })
            .catch(() => {
                setCustomerRequirements({
                    requireBranchInCustomer: false,
                    requireCpfInCustomer: false
                });
            });
    }, []);

    // Define o formik.
    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
            document: '',
            phone: '',
            gender: null,
            birthDate: null,
            branch: selectedBranch ? { value: selectedBranch.id, label: selectedBranch.name } : null
        },
        enableReinitialize: true, // Importante para atualizar se o selectedBranch mudar (embora raro na mesma sessão sem refresh)
        validationSchema: formSchema,
        onSubmit: async values => {
            let gender = null;
            if (values.gender !== null) {
                gender = values.gender.value;
            }

            let branch = null;
            if (values.branch !== null) {
                branch = {
                    id: values.branch.value
                }
            }

            let entity = {
                id: getId(entityData),
                firstName: values.firstName,
                lastName: values.lastName,
                phone: values.phone,
                gender: gender,
                birthDate: values.birthDate ? formatDateToYYYYMMDD(values.birthDate) : null,
                status: convertBooleanToStatus(status),
                document: values.document,
                email: values.email,
                branch: branch
            }

            // Prepara os dados.
            prepareUploadFileData(uploadedFile, entity, "main/customers",
                entityData).then(async data => {
                    try {
                        const resp = await sendUploadFile(Api.main.customer + "/upload", entity.id, data);

                        successAlert(t('message.operation_successfully'), () => {
                            if (entityData == null) {
                                navigate(`${MenuConfig.main.customer.route}/new/${resp.data.id}`);
                            } else {
                                navigate(MenuConfig.main.customer.route);
                            }
                        });
                    } catch (error) {
                        ApiError(error);
                        formik.setSubmitting(false);
                    }
                });
        },
    });

    // Valida o form e em seguida submete os dados. Aciona o onSubmit do formik.
    const onSubmit = (e) => {
        e.preventDefault();
        formik.handleSubmit();
    }

    return (
        <>
            <NewPage
                motherMenu={t('main')}
                childMenu={t('customers')}
                activeMenu={t('new')}
                child={
                    <Form
                        formik={formik}
                        status={status}
                        setStatus={setStatus}
                        entityData={entityData}
                        setUploadedFile={setUploadedFile}
                        uploadedFile={uploadedFile}
                        isBranchFixed={!!selectedBranch}
                        requireBranchInCustomer={customerRequirements.requireBranchInCustomer}
                        requireCpfInCustomer={customerRequirements.requireCpfInCustomer}
                    />
                }
                setEntityData={setEntityData}
                path={Api.main.customer}
                isSubmitting={formik.isSubmitting}
                handleSubmit={onSubmit}
                cancelRoute={MenuConfig.main.customer.route}
            />
        </>
    );
}

export default CustomerNew;
